import { useEffect, useRef } from "react";
import { toast } from "sonner";
import axios, { API } from "@/lib/api";

// Headless: on the desktop POS, when an update is READY and the till is idle, restart the app into the new version
// by itself. Shops never press "restart now" and power the PC off at night, so tills sat on 1.1.329 for weeks
// (Leyenda 2026-10-21). Served by the web bundle → reaches every PC the same day, whatever desktop build it runs.
// Desktop ≥1.1.346 exposes installUpdate(); older builds get window.close() → electron-updater install-on-quit →
// the new installer relaunches the POS (installer.nsh customInstall).
const cmp = (a, b) => { const x = String(a || "").split(".").map(Number), y = String(b || "").split(".").map(Number); for (let i = 0; i < 3; i++) { if ((x[i] || 0) !== (y[i] || 0)) return (x[i] || 0) - (y[i] || 0); } return 0; };
const cartEmpty = () => { const c = localStorage.getItem("sellox_pos_cart"); return !c || c === "[]"; };
const loggedIn = () => !!(localStorage.getItem("token") || localStorage.getItem("pos_token"));

export function DesktopAutoUpdate() {
  const lastInput = useRef(Date.now());
  const lastCart = useRef({ v: localStorage.getItem("sellox_pos_cart") || "", at: Date.now() });
  const postponedUntil = useRef(0);
  const armed = useRef("");
  useEffect(() => {
    const d = window.selloxDesktop;
    if (!d?.getUpdateStatus) return;
    const touch = () => { lastInput.current = Date.now(); };
    for (const ev of ["mousedown", "keydown", "touchstart", "wheel"]) window.addEventListener(ev, touch, { passive: true });
    let policy = null;
    const loadPolicy = () => loggedIn() && axios.get(`${API}/desktop/update-policy`, { skipGlobalError: true, timeout: 8000 }).then(r => { policy = r.data; }).catch(() => {});
    const install = async (version) => {
      try { if (d.installUpdate) { const r = await d.installUpdate(); if (r?.ok) return; } } catch {}
      window.close(); // pre-1.1.346: install-on-quit; the new installer brings the POS back
    };
    const tick = async () => {
      try {
        const c = localStorage.getItem("sellox_pos_cart") || "";
        if (c !== lastCart.current.v) lastCart.current = { v: c, at: Date.now() };
        const st = await d.getUpdateStatus();
        if (!st || st.state !== "ready" || !st.version) return;
        const cur = await (d.getVersion ? d.getVersion() : Promise.resolve(d.version));
        if (cur && cmp(st.version, cur) <= 0) return;
        if (cmp(st.version, "1.1.346") < 0) return; // older installers do not relaunch the POS after a silent install
        if (st.portable) return; // portable zip applies itself at the next boot
        if (!policy) await loadPolicy();
        if (!policy || policy.auto_restart === false) return;
        if (Date.now() < postponedUntil.current || armed.current === st.version) return;
        const idleMs = (policy.idle_min || 5) * 60000;
        const idle = Date.now() - lastInput.current > idleMs && Date.now() - lastCart.current.at > idleMs;
        if (!cartEmpty() || !(idle || !loggedIn())) return;
        armed.current = st.version;
        const secs = policy.countdown_s || 20;
        const id = `desktop-update-${st.version}`;
        toast.info(`🔄 新版 v${st.version} 已下载，${secs} 秒后自动重启更新（约 40 秒，不影响数据） / Actualizando a v${st.version} en ${secs} s`, {
          id, duration: secs * 1000,
          action: { label: "稍后 1 小时 / Después", onClick: () => { postponedUntil.current = Date.now() + 3600000; armed.current = ""; toast.dismiss(id); } },
        });
        setTimeout(() => { if (Date.now() < postponedUntil.current) return; install(st.version); }, secs * 1000);
      } catch {}
    };
    const t = setInterval(tick, 30000);
    setTimeout(tick, 15000);
    return () => { clearInterval(t); for (const ev of ["mousedown", "keydown", "touchstart", "wheel"]) window.removeEventListener(ev, touch); };
  }, []);
  return null;
}
